import React, { useEffect, useRef, useMemo } from "react";
import { MapContainer, TileLayer, Marker, Polyline, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import { fmtTime } from "@/lib/datetime";
import { STATUS_HEX, STATUS_LABEL, personInitials, effectiveStatus, markerColor } from "@/lib/orderStatus";
import { formatPhone } from "@/lib/phone";
import { SOUTH_FLORIDA_CENTER, regionCenter, withMinMapBounds } from "@/lib/mapBounds";

// Fix default marker icons (Leaflet asset path issue in webpack)
const defaultIcon = new L.Icon({
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

const PICKUP_COLOR = "#111111"; // black — distinct visual for pickup orders so dispatch & driver can quickly differentiate them from deliveries

// Labels used inside the stop popup header. Keeps popup consistency with the
// Overview map & the New-Order form so the same value renders the same way
// everywhere in the app.
const DELIVER_TYPE_LABEL = {
  same_day: "Same Day",
  next_day: "Next Day",
  otc: "OTC",
  pickup: "Pick Up",
};
const PRIORITY_LABEL = {
  normal: "Normal",
  urgent: "Urgent",
  cold_chain: "Cold Chain",
  narcotic: "Narcotic",
  clinic: "Clinic",
  business: "Business",
};

// Small red-flag corner badge overlaid on the top-left of a stop pin when the
// order lands in a restricted ZIP code. Pharmacy staff / dispatch can spot
// these visually before opening the drawer. Kept tiny (14×14) so it doesn't
// obscure the pin's initials.
const RESTRICTED_FLAG_HTML = `<span
  style="position:absolute;top:-6px;left:-6px;background:#DC2626;color:#fff;font-size:10px;font-weight:900;width:16px;height:16px;border-radius:3px;display:flex;align-items:center;justify-content:center;border:1.5px solid #fff;box-shadow:0 1px 3px rgba(0,0,0,.35);line-height:1;"
  title="Order in a Restricted ZIP"
>&#9873;</span>`;

const stopIcon = (initials, color = "#3a86ff", seq = null, isPickup = false, isRestricted = false, coSeqs = null) => {
  // Second-stop-at-same-address indicator — red pill on the LEFT showing
  // the OTHER sequence(s) sharing this exact lat/lng, so dispatch and
  // drivers instantly see multiple deliveries at the same door.
  const coBadge = coSeqs && coSeqs.length
    ? `<span style="position:absolute;top:-6px;left:-6px;background:#DC2626;color:#fff;font-size:9px;font-weight:800;min-width:16px;height:16px;padding:0 4px;border-radius:50%;display:flex;align-items:center;justify-content:center;border:1.5px solid #fff;box-shadow:0 1px 3px rgba(0,0,0,.35);" title="Also stops #${coSeqs.join(', #')} at this address">${coSeqs.join('/')}</span>`
    : "";
  if (isPickup) {
    // Pickup pin: black circle + "P" badge to clearly distinguish from delivery circles
    return L.divIcon({
      className: "",
      html: `<div style="
        position:relative;
        background:${PICKUP_COLOR};
        color:#fff;
        width:29px;height:29px;
        border-radius:50%;
        display:flex;align-items:center;justify-content:center;
        font-weight:800;font-size:10px;letter-spacing:.3px;
        box-shadow:0 4px 10px rgba(0,0,0,.55);
        border:2px solid #fff;
        transform:rotate(0deg);
      ">${initials}
        <span style="position:absolute;top:-7px;left:-7px;background:#fff;color:#111;font-size:9px;font-weight:900;width:18px;height:18px;border-radius:50%;display:flex;align-items:center;justify-content:center;border:1.5px solid #111;letter-spacing:0;">P</span>
        ${seq != null ? `<span style="position:absolute;top:-6px;right:-6px;background:#3a86ff;color:#fff;font-size:9px;width:16px;height:16px;border-radius:50%;display:flex;align-items:center;justify-content:center;border:1.5px solid #fff;">${seq}</span>` : ""}
      </div>`,
      iconSize: [29, 29],
      iconAnchor: [14, 14],
    });
  }
  return L.divIcon({
    className: "",
    html: `<div style="
      position:relative;
      background:${color};
      color:#fff;
      width:29px;height:29px;
      border-radius:50%;
      display:flex;align-items:center;justify-content:center;
      font-weight:800;font-size:10px;letter-spacing:.3px;
      box-shadow:0 4px 8px rgba(0,0,0,.2);
      border:2px solid #fff;
    ">${initials}${isRestricted ? RESTRICTED_FLAG_HTML : ""}${coBadge}${seq != null ? `<span style="position:absolute;top:-6px;right:-6px;background:#111;color:#fff;font-size:9px;width:16px;height:16px;border-radius:50%;display:flex;align-items:center;justify-content:center;border:1.5px solid #fff;">${seq}</span>` : ""}</div>`,
    iconSize: [29, 29],
    iconAnchor: [14, 14],
  });
};

// Default center: midpoint of the operator's standard South Florida service area
// (Boca Raton → Homestead). Single source of truth lives in lib/mapBounds.js.
const DEFAULT_CENTER = SOUTH_FLORIDA_CENTER;

const initialsOf = (name) => personInitials(name, "PH");

const startIcon = (name) => L.divIcon({
  className: "",
  html: `<div style="
    background:#111;color:#fff;width:31px;height:31px;border-radius:6px;
    display:flex;align-items:center;justify-content:center;font-weight:800;font-size:11px;
    letter-spacing:.5px;box-shadow:0 4px 8px rgba(0,0,0,.25);border:3px solid #fff;
  ">${initialsOf(name)}</div>`,
  iconSize: [31, 31],
  iconAnchor: [15, 15],
});

const driverIcon = L.divIcon({
  className: "",
  html: `<div style="position:relative;width:36px;height:36px;">
    <div style="position:absolute;inset:0;background:#3a86ff;border-radius:50%;opacity:.25;animation:pulse 1.6s ease-out infinite;"></div>
    <div style="position:absolute;inset:6px;background:#3a86ff;border-radius:50%;display:flex;align-items:center;justify-content:center;color:#fff;font-size:14px;border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.3);">🚐</div>
  </div>
  <style>@keyframes pulse{0%{transform:scale(.6);opacity:.5;}100%{transform:scale(1.6);opacity:0;}}</style>`,
  iconSize: [36, 36],
  iconAnchor: [18, 18],
});

function FitBounds({ points, region, fitToStopsOnly }) {
  const map = useMap();
  useEffect(() => {
    const valid = (points || []).filter((p) => {
      if (!p) return false;
      const lat = Array.isArray(p) ? p[0] : p.lat;
      const lng = Array.isArray(p) ? p[1] : p.lng;
      return typeof lat === "number" && typeof lng === "number";
    });
    if (fitToStopsOnly) {
      // Driver-facing view: fit tightly to actual stops so the northernmost,
      // southernmost, easternmost and westernmost stop are all visible. Skip
      // the workspace-wide bounds floor used on the dispatcher / admin maps.
      if (valid.length === 0) return;
      if (valid.length === 1) {
        // Single stop → center on it at neighborhood zoom so it isn't
        // rendered as a lonely dot on a huge map.
        const [lat, lng] = Array.isArray(valid[0]) ? valid[0] : [valid[0].lat, valid[0].lng];
        map.setView([lat, lng], 15, { animate: false });
        return;
      }
      const bounds = L.latLngBounds(valid.map((p) => (Array.isArray(p) ? p : [p.lat, p.lng])));
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 16 });
      return;
    }
    // Dispatcher / admin default: always extend the standard workspace
    // service window so a tight route never zooms in past a single city.
    const bounds = withMinMapBounds(valid, region);
    map.fitBounds(bounds, { padding: [12, 12] });
  }, [points, map, region, fitToStopsOnly]);
  return null;
}

export default function MapView({ start, stops = [], polyline = [], height = 480, onStopClick, driverLocation = null, routeStatus = null, region = "south_fl", fitToStopsOnly = false, routeLabel = null }) {
  const center = start ? [start.lat, start.lng] : stops[0] ? [stops[0].lat, stops[0].lng] : regionCenter(region);
  const allPoints = [
    ...(start ? [[start.lat, start.lng]] : []),
    ...stops.map((s) => [s.lat, s.lng]),
    ...(driverLocation ? [[driverLocation.lat, driverLocation.lng]] : []),
  ];

  // Group stops by rounded lat/lng so co-located orders (same door) share a
  // single marker with a "2nd stop here" red badge. 5-decimal rounding
  // ≈ 1.1 m — tight enough to treat only true duplicates as co-located.
  const coLocated = useMemo(() => {
    const buckets = new Map();
    stops.forEach((s, idx) => {
      if (s.lat == null || s.lng == null) return;
      const key = `${s.lat.toFixed(5)},${s.lng.toFixed(5)}`;
      if (!buckets.has(key)) buckets.set(key, []);
      buckets.get(key).push({ idx, seq: s.sequence ?? idx + 1, stop: s });
    });
    return buckets;
  }, [stops]);
  const coLocatedKey = (s) => (s.lat == null || s.lng == null ? null : `${s.lat.toFixed(5)},${s.lng.toFixed(5)}`);

  return (
    <div className="rounded-xl overflow-hidden border border-border" style={{ height }} data-testid="map-view">
      <MapContainer center={center} zoom={10} zoomSnap={0} style={{ height: "100%", width: "100%" }} scrollWheelZoom>
        <TileLayer
          attribution='&copy; OpenStreetMap'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {start && (
          <Marker position={[start.lat, start.lng]} icon={startIcon(start.name)}>
            <Popup>
              <div className="font-medium">{start.name || "Pharmacy"}</div>
              <div className="text-xs text-muted-foreground">Start point</div>
            </Popup>
          </Marker>
        )}
        {stops.map((s, idx) => {
          const initials = personInitials(s.customer_name);
          // Determine status: prefer explicit, then route context (planned→assigned, in_progress→in_transit), else effective fallback
          let st = s.status;
          if (!st) {
            if (routeStatus === "in_progress") st = "in_transit";
            else if (routeStatus === "planned" || polyline.length > 1) st = "assigned";
            else st = effectiveStatus(s);
          }
          // Previously-failed orders stay RED until they're actually delivered,
          // even when they've been re-attached to a new route (status flipped
          // back to "assigned" / "in_transit"). This gives dispatchers an
          // at-a-glance view of which stops are on a 2nd attempt.
          const prevFailed = (s.failed_count || 0) > 0 && st !== "delivered";
          const color = prevFailed ? STATUS_HEX.failed : markerColor(s, st);
          const isPickup = (s.deliver_type || "").toLowerCase() === "pickup" || !!s.is_pickup;
          // Red flag shows ONLY while the restricted-ZIP order is still on
          // hold / awaiting approval. Once Super Admin approves it the flag
          // disappears (rejected orders are hard-deleted and never reach
          // the map). `s.restricted` is a legacy alias kept for back-compat.
          const isRestricted = !!(s.restricted_zip || s.restricted) && (s.approval_status !== "approved");
          // Any OTHER sequences that share this exact address.
          const groupKey = coLocatedKey(s);
          const mySeq = s.sequence ?? idx + 1;
          const coSeqs = groupKey
            ? (coLocated.get(groupKey) || [])
                .filter((e) => e.seq !== mySeq)
                .map((e) => e.seq)
            : [];
          return (
            <Marker
              key={s.order_id || idx}
              position={[s.lat, s.lng]}
              icon={stopIcon(initials, color, mySeq, isPickup, isRestricted, coSeqs)}
              eventHandlers={{ click: () => onStopClick && onStopClick(s) }}
            >
              <Popup>
                {/* Consistent header: Pharmacy · Deliver Type · Priority so
                    dispatch always sees the same shape when clicking any pin. */}
                <div className="text-[11px] font-semibold text-black mb-1" data-testid={`popup-header-${s.order_id}`}>
                  {[
                    s.pharmacy_nickname || "Pharmacy",
                    DELIVER_TYPE_LABEL[s.deliver_type] || (s.deliver_type ? s.deliver_type : "Same Day"),
                    PRIORITY_LABEL[s.priority] || (s.priority ? s.priority : "Normal"),
                  ].join(" - ")}
                </div>
                <div className="font-semibold text-black uppercase tracking-wide" data-testid={`popup-name-${s.order_id}`}>
                  {s.customer_name}
                </div>
                {s.address && (
                  <div className="text-xs text-black/80 mt-0.5" data-testid={`popup-address-${s.order_id}`}>
                    {s.address}
                  </div>
                )}
                {s.phone && (
                  <div className="text-xs text-black/80" data-testid={`popup-phone-${s.order_id}`}>
                    {formatPhone(s.phone)}
                  </div>
                )}
                {routeLabel && (
                  <div className="text-[11px] font-bold text-black/70 mt-1 uppercase tracking-wider" data-testid={`popup-route-${s.order_id}`}>
                    ON ROUTE: {routeLabel} · stop {s.sequence ?? idx + 1}
                  </div>
                )}
                {coSeqs.length > 0 && (
                  <div className="mt-1 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded" style={{ background: "#DC2626", color: "#fff" }} data-testid={`popup-cosequences-${s.order_id}`}>
                    Also stop{coSeqs.length > 1 ? "s" : ""} #{coSeqs.join(", #")} here
                  </div>
                )}
                {isPickup && (
                  <div className="mt-1 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded" style={{ background: "#111", color: "#fff" }}>
                    Pick Up
                  </div>
                )}
                <div className="text-[10px] uppercase tracking-wider mt-1" style={{ color }}>{STATUS_LABEL[st] || st}</div>
                {prevFailed && st !== "failed" && (
                  <div className="text-[10px] font-semibold mt-0.5" style={{ color: STATUS_HEX.failed }}>
                    2ND ATTEMPT — previously failed
                  </div>
                )}
              </Popup>
            </Marker>
          );
        })}
        {driverLocation && (
          <Marker position={[driverLocation.lat, driverLocation.lng]} icon={driverIcon}>
            <Popup>
              <div className="font-medium">Driver</div>
              <div className="text-xs text-muted-foreground">Last update: {fmtTime(driverLocation.at)}</div>
            </Popup>
          </Marker>
        )}
        {polyline?.length > 1 && (
          <Polyline positions={polyline} pathOptions={{ color: "#d62828", weight: 4, opacity: 0.85 }} />
        )}
        <FitBounds points={allPoints} region={region} fitToStopsOnly={fitToStopsOnly} />
      </MapContainer>
    </div>
  );
}
